/**
 * src/pages/WhatIsConsciousness/WhatIsConsciousness.tsx
 * Entry page for "What Is Consciousness?" (Journey Question 02).
 * Educational, cinematic, dark cosmic exploration of mind, brain, and subjective awareness.
 */

import React from "react";
import { OriginPageShell } from "@/components/originBeyond/OriginPageShell";
import { PageHero } from "@/components/originBeyond/PageHero";
import { EvidenceKey } from "@/components/originBeyond/EvidenceKey";
import { DisclaimerNote } from "@/components/originBeyond/DisclaimerNote";
import { RelatedExperiences } from "@/components/originBeyond/RelatedExperiences";
import { JourneyBar } from "@/components/originBeyond/JourneyBar";
import { SourceList } from "@/components/originBeyond/SourceList";
import { NeuralLatticeVisual } from "./sections/NeuralLatticeVisual";
import { BrainExplorer } from "./sections/BrainExplorer";
import { HardProblemSection } from "./sections/HardProblemSection";
import { TheoriesComparison } from "./sections/TheoriesComparison";
import { MemoryDemo } from "./sections/MemoryDemo";
import { IdentityContinuity } from "./sections/IdentityContinuity";
import { StillUnanswered } from "./sections/StillUnanswered";
import { CONSCIOUSNESS_SOURCES } from "./consciousnessData";
import "./WhatIsConsciousness.module.css";
import "@/components/cosmic/cosmic.css";
export default function WhatIsConsciousnessPage() {
  return <OriginPageShell accent="violet">
      <div className="w-full max-w-full min-w-0 flex flex-col gap-12 sm:gap-16 lg:gap-20">
        {/* 1. Page Hero with signature animated neural lattice visual */}
        <PageHero questionNumber={2} eyebrow="Epistemic Inquiry: Mind & Matter" title="What Is Consciousness?" subtitle="At every moment of waking life, you inhabit an inner theater of sensations, emotions, and thoughts—what philosopher Thomas Nagel called 'what it is like' to be an experiencing being. How does three pounds of electrochemical tissue generate the vivid panorama of subjective reality?" cta={{
        label: "Explore Brain Models",
        href: "#brain-explorer-heading"
      }}>
          <NeuralLatticeVisual />
        </PageHero>

        {/* Content container */}
        <div className="max-w-[76rem] mx-auto w-full px-4 sm:px-6 lg:px-8 space-y-16 sm:space-y-20 lg:space-y-24 min-w-0">
          {/* Epistemic guide key */}
          <EvidenceKey title="Evidence & Epistemic Standards" levels={["established", "research", "philosophy", "speculative"]} />

          {/* 2. Interactive Brain Explorer */}
          <BrainExplorer />

          {/* 3. The Hard Problem & Explanatory Gap */}
          <HardProblemSection />

          {/* 4. Theories of Consciousness TabExplorer */}
          <TheoriesComparison />

          {/* 5. Memory & Reconstructive Selfhood Interactive Demo */}
          <MemoryDemo />

          {/* 6. Identity & Ship of Theseus Slider */}
          <IdentityContinuity />

          {/* 7. Still Unanswered Questions Accordion */}
          <StillUnanswered />

          {/* 8. Scientific Integrity DisclaimerNote */}
          <DisclaimerNote tone="science" title="Scientific Integrity: No Consensus Theory of Consciousness">
            Science has charted exquisite correlations between specific neural circuits, neurotransmitters,
            and conscious percepts. However, there is presently no universally accepted mathematical or physical
            theory explaining how subjective experience emerges from matter. Competing models—including Global Workspace,
            Integrated Information, and Predictive Processing—remain active areas of empirical debate. Quantum brain
            hypotheses are speculative and lack confirmation; quantum physics does not establish that consciousness
            survives physical death or controls reality.
          </DisclaimerNote>

          {/* 9. Related Quantum Liaison Experiences */}
          <RelatedExperiences keys={["selves", "lab", "whatIf", "creator"]} title="Explore Related Quantum Liaison Portals" />

          {/* 10. Persistent Journey Bar */}
          <JourneyBar current="consciousness" />

          {/* 11. Scholarly Source List */}
          <SourceList sources={CONSCIOUSNESS_SOURCES} title="Scholarly & Archival Sources" />
        </div>
      </div>
    </OriginPageShell>;
}